import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, Eye, FileCheck2, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatUsd, fmtDateOnly } from '../lib/format';
import { parsearNumero } from '../lib/numeroUS';
import { abrirEnPestana } from '../lib/imprimirPdf';
import { useDialog } from '../lib/useDialog';
import type { CocState } from './ClaimCocTab';

/**
 * LA VENTANA DE "GENERAR COC" (dueño 2026-10-28): "con amount te vas a buscar el estimado más alto que tenemos
 * del seguro y el de nosotros, colocas los dos ahí, sale una pantalla en el medio y ese es el monto que vamos a
 * seleccionar, o un monto diferente que yo pueda escribir… el build date, si no lo tenemos, pregunta por el
 * build date".
 *
 * Los montos y el build date (guardado o de JobFlow) los trae el servidor (`coc/generado.ts`); las reglas
 * (fecha obligatoria y no futura, monto > 0) también viven allá: acá solo se avisa antes de mandar.
 */
type OpcionMonto = { cents: number; label: string; date: string | null; file_id: string | null };
type Opciones = {
  insurance: OpcionMonto | null;
  ours: OpcionMonto | null;
  build_date: string | null;
  build_date_source: 'saved' | 'jobflow' | null;
  claim_ref: { kind: 'insurance' | 'proactive' | null; value: string; label: string };
  homeowner_name: string | null;
  contractor_name: string | null;
  coc_sent: boolean;
  today: string;
};
export type CocGenerado = { state: CocState; file: { id: string; file_name: string }; linked: boolean };
type Fuente = 'insurance' | 'ours' | 'custom';

const ERRORES: Record<string, string> = {
  build_date_required: 'coc.genErrBuildDate',
  build_date_in_future: 'coc.genErrFuture',
  invalid_amount: 'coc.genErrAmount',
};

export function CocGenerateModal({ claimId, withAmount, onClose, onGenerated }: {
  claimId: string;
  withAmount: boolean;
  onClose: () => void;
  onGenerated: (r: CocGenerado) => void;
}) {
  const { t } = useI18n();
  const dialogo = useDialog(onClose, { autoFocus: false });
  const [op, setOp] = useState<Opciones | null>(null);
  const [cargaFallo, setCargaFallo] = useState(false);
  const [fuente, setFuente] = useState<Fuente>('insurance');
  const [otro, setOtro] = useState('');
  const [buildDate, setBuildDate] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api.get<Opciones>(`/api/claims/${claimId}/coc/generate`).then((r) => {
      if (!vivo) return;
      setOp(r);
      setBuildDate(r.build_date ?? '');
      // Arranca en el del seguro si hay; si no, el nuestro; si no hay ninguno, el monto a mano.
      setFuente(r.insurance ? 'insurance' : r.ours ? 'ours' : 'custom');
    }).catch(() => { if (vivo) setCargaFallo(true); });
    return () => { vivo = false; };
  }, [claimId]);

  // Un aviso viejo ("Enter the build date.") no puede quedar en rojo después de que la persona lo arregló.
  useEffect(() => { setErr(null); }, [buildDate, fuente, otro]);

  const montoCents = useMemo((): number | null => {
    if (!withAmount || !op) return null;
    if (fuente === 'insurance') return op.insurance?.cents ?? null;
    if (fuente === 'ours') return op.ours?.cents ?? null;
    const n = parsearNumero(otro);
    return n != null && n > 0 ? Math.round(n * 100) : null;
  }, [withAmount, op, fuente, otro]);

  // Lo que la pantalla puede decir antes de mandar (el servidor vuelve a validar).
  const problema = !buildDate
    ? 'coc.genErrBuildDate'
    : op && buildDate > op.today
      ? 'coc.genErrFuture'
      : withAmount && montoCents == null
        ? 'coc.genErrAmount'
        : null;

  const vistaPrevia = () => {
    if (problema) { setErr(t(problema)); return; }
    const q = new URLSearchParams({ build_date: buildDate });
    if (montoCents != null) q.set('amount_cents', String(montoCents));
    // La URL ya se sabe: la pestaña se abre DENTRO del clic, sin await de por medio.
    abrirEnPestana(`/api/claims/${claimId}/coc/generate.pdf?${q.toString()}`);
  };

  const generar = async () => {
    if (problema) { setErr(t(problema)); return; }
    setEnviando(true); setErr(null);
    try {
      const r = await api.post<CocGenerado>(`/api/claims/${claimId}/coc/generate`, {
        amountCents: montoCents,
        amountSource: withAmount ? fuente : null,
        buildDate,
      });
      onGenerated(r);
    } catch (e) {
      setErr(t((e instanceof ApiError && e.code && ERRORES[e.code]) || 'coc.genErr'));
    } finally { setEnviando(false); }
  };

  const Opcion = ({ valor, titulo, o }: { valor: Fuente; titulo: string; o: OpcionMonto | null }) => {
    const activo = fuente === valor;
    return (
      <label style={{
        display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, cursor: o ? 'pointer' : 'not-allowed',
        border: `1px solid ${activo ? 'var(--accent)' : 'var(--line)'}`, background: activo ? 'var(--surface-2)' : 'transparent', opacity: o ? 1 : 0.55,
      }}>
        <input type="radio" name="coc-monto" checked={activo} disabled={!o} onChange={() => setFuente(valor)} style={{ marginTop: 3 }} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)', display: 'block' }}>{titulo}</span>
          {o ? (
            <>
              <strong style={{ fontSize: 'var(--fs-md)', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>{formatUsd(o.cents)}</strong>
              {/* El nombre del archivo es largo ("Insurance Estimate 2 - Nombre - Claim"): se corta, y la fecha va
                  en su renglón para que se vea siempre cuál es. */}
              <span className="muted" title={o.label} style={{ fontSize: 'var(--fs-2xs)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {o.label}
              </span>
              {o.date && <span className="muted" style={{ fontSize: 'var(--fs-2xs)', display: 'block' }}>{fmtDateOnly(o.date)}</span>}
            </>
          ) : <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('coc.genNone')}</span>}
        </span>
      </label>
    );
  };

  const fuenteDeLaFecha = op?.build_date_source === 'saved' && buildDate === op.build_date
    ? t('coc.genBuildSaved')
    : op?.build_date_source === 'jobflow' && buildDate === op.build_date
      ? t('coc.genBuildJobflow')
      : !op?.build_date ? t('coc.genBuildMissing') : null;

  const ui = (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div ref={dialogo.ref} {...dialogo.props} className="card" onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(560px, 96vw)', maxHeight: '92vh', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--line)' }}>
          <FileCheck2 size={16} strokeWidth={1.75} />
          <b style={{ fontSize: 'var(--fs-md)' }}>{t(withAmount ? 'coc.genModalWith' : 'coc.genModalWithout')}</b>
          <span style={{ flex: 1 }} />
          <button type="button" className="btn ghost sm" onClick={onClose} aria-label={t('common.close')}><X size={14} strokeWidth={1.75} /></button>
        </div>

        <div style={{ padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!op && !cargaFallo && <div className="muted">{t('common.loadingDots')}</div>}
          {cargaFallo && <div className="badge red" style={{ alignSelf: 'flex-start' }}>{t('coc.genErr')}</div>}
          {op && (
            <>
              {op.coc_sent && <div className="badge amber" style={{ whiteSpace: 'normal', lineHeight: 1.4 }}>{t('coc.genAlreadySent')}</div>}
              {op.claim_ref.kind !== 'insurance' && (
                <div className="badge amber" style={{ whiteSpace: 'normal', lineHeight: 1.4 }}>
                  {t('coc.genNoClaimNumber', { n: op.claim_ref.value || '—' })}
                </div>
              )}
              {!op.contractor_name && <div className="badge amber" style={{ whiteSpace: 'normal', lineHeight: 1.4 }}>{t('coc.genNoContractor')}</div>}

              {withAmount && (
                <div>
                  <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', marginBottom: 8 }}>{t('coc.genPickAmount')}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                    <Opcion valor="insurance" titulo={t('coc.genInsurance')} o={op.insurance} />
                    <Opcion valor="ours" titulo={t('coc.genOurs')} o={op.ours} />
                  </div>
                  <label style={{
                    display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                    border: `1px solid ${fuente === 'custom' ? 'var(--accent)' : 'var(--line)'}`, background: fuente === 'custom' ? 'var(--surface-2)' : 'transparent',
                  }}>
                    <input type="radio" name="coc-monto" checked={fuente === 'custom'} onChange={() => setFuente('custom')} />
                    <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('coc.genCustom')}</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span className="muted">$</span>
                      <input className="input sm" inputMode="decimal" placeholder="0.00" value={otro}
                        onFocus={() => setFuente('custom')} onChange={(e) => { setOtro(e.target.value); setFuente('custom'); }}
                        style={{ width: 140, fontFamily: 'var(--font-mono)', textAlign: 'right' }} />
                    </span>
                  </label>
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', marginBottom: 6 }}>
                  <CalendarDays size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('coc.genBuildDate')}
                </label>
                <input className="input" type="date" value={buildDate} max={op.today} onChange={(e) => setBuildDate(e.target.value)} style={{ width: 200 }} />
                {fuenteDeLaFecha && <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 4 }}>{fuenteDeLaFecha}</div>}
              </div>

              <div className="muted" style={{ fontSize: 'var(--fs-2xs)', lineHeight: 1.5 }}>{t('coc.genWhatHappens')}</div>
              {err && <div className="badge red" style={{ alignSelf: 'flex-start', whiteSpace: 'normal' }}>{err}</div>}
            </>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderTop: '1px solid var(--line)' }}>
          <button type="button" className="btn ghost sm" onClick={vistaPrevia} disabled={!op || enviando}>
            <Eye size={14} strokeWidth={1.75} /> {t('coc.genPreview')}
          </button>
          <span style={{ flex: 1 }} />
          <button type="button" className="btn ghost sm" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="btn sm" onClick={generar} disabled={!op || enviando}>
            <FileCheck2 size={14} strokeWidth={1.75} /> {enviando ? t('coc.genGenerating') : t('coc.genGenerate')}
          </button>
        </div>
      </div>
    </div>
  );
  return createPortal(ui, document.body);
}
